/**
 * Native threshold free-gift engine.
 *
 * Adds a configured gift variant to the cart once the cart subtotal (excluding
 * the gift itself and any other free-gift lines) reaches a threshold, and
 * removes it again if the cart drops back below the threshold.
 *
 * Config is rendered server-side into <script id="el-free-gift-data"> from
 * Theme settings → Cart (enable / product / threshold).
 *
 * The gift line is tagged with `_bundle_free: '1'` so it reuses the existing
 * cart-drawer free-gift rendering (badge, $0 price, hidden quantity, excluded
 * from the empty-state check).
 *
 * Triggers: drawer open, page load, and cart quantity changes.
 */
(function () {
  var DRAWER_SECTIONS = ['el-cart-drawer', 'el-cart-icon-bubble'];
  var GIFT_PROPERTY = '_bundle_free';

  var _syncRunning = false;
  var _drawerSyncTimer = null;

  function getConfig() {
    var el = document.getElementById('el-free-gift-data');
    if (!el) return null;
    try {
      var cfg = JSON.parse(el.textContent || '{}');
      if (!cfg || !cfg.enabled || !cfg.giftVariantId) return null;
      return cfg;
    } catch (_) {
      return null;
    }
  }

  function isGiftLine(item, config) {
    var props = item.properties || {};
    return (
      String(item.variant_id) === String(config.giftVariantId) &&
      String(props[GIFT_PROPERTY]) === '1'
    );
  }

  function findGiftLine(cart, config) {
    return (cart.items || []).find(function (item) {
      return isGiftLine(item, config);
    });
  }

  // Cart subtotal excluding the gift and any other free-gift lines.
  function qualifyingSubtotal(cart) {
    return (cart.items || []).reduce(function (total, item) {
      var props = item.properties || {};
      if (String(props[GIFT_PROPERTY]) === '1') return total;
      return total + (item.final_line_price || 0);
    }, 0);
  }

  function fetchConfigFn() {
    if (typeof fetchConfig === 'function') return fetchConfig();
    return { method: 'POST', headers: { 'Content-Type': 'application/json' } };
  }

  function addGift(config, sections, sectionsUrl) {
    var body = {
      items: [
        {
          id: config.giftVariantId,
          quantity: 1,
          properties: {
            _bundle_free: '1',
            _bundle_price_cents: String(config.compareAtCents || 0),
          },
        },
      ],
      sections: sections,
      sections_url: sectionsUrl,
    };

    // Use the .js endpoint — routes.cart_add_url is /cart/add and can return HTML.
    return fetch(
      '/cart/add.js',
      Object.assign({}, fetchConfigFn(), { body: JSON.stringify(body) })
    );
  }

  function removeGift(giftLine, sections, sectionsUrl) {
    var body = {
      id: giftLine.key,
      quantity: 0,
      sections: sections,
      sections_url: sectionsUrl,
    };

    // Use the .js endpoint — routes.cart_change_url is /cart/change and can return HTML.
    return fetch(
      '/cart/change.js',
      Object.assign({}, fetchConfigFn(), { body: JSON.stringify(body) })
    );
  }

  // Returns the resulting cart state (with rendered sections) if a change was
  // made, otherwise null.
  async function reconcile(cart, sections, sectionsUrl) {
    var config = getConfig();
    if (!config) return null;
    if (_syncRunning) return null;

    var giftLine = findGiftLine(cart, config);
    var subtotal = qualifyingSubtotal(cart);
    var meetsThreshold = subtotal >= config.thresholdCents;

    // Nothing to do.
    if (meetsThreshold && giftLine) return null;
    if (!meetsThreshold && !giftLine) return null;

    _syncRunning = true;
    try {
      var response = meetsThreshold
        ? await addGift(config, sections, sectionsUrl)
        : await removeGift(giftLine, sections, sectionsUrl);

      if (response.status === 429) {
        window.location.reload();
        return null;
      }

      var state = await response.json();
      if (!response.ok || state.status === 'bad_request' || state.status === 422) {
        console.error('[FreeGift] request failed', state);
        return null;
      }

      return state;
    } finally {
      _syncRunning = false;
    }
  }

  // Replace the cart-drawer + icon-bubble markup from a sections payload,
  // without a full page reload.
  window.applyFreeGiftCartSections = function (state) {
    if (!state || !state.sections) return;

    var drawer = document.querySelector('cart-drawer');
    var cartDrawerItems = document.querySelector('cart-drawer-items');

    if (typeof state.item_count === 'number') {
      if (drawer) drawer.classList.toggle('is-empty', state.item_count === 0);
      if (cartDrawerItems) cartDrawerItems.classList.toggle('is-empty', state.item_count === 0);
    }

    var drawerHtml = state.sections['el-cart-drawer'];
    if (drawerHtml) {
      var drawerDoc = new DOMParser().parseFromString(drawerHtml, 'text/html');
      var newCartDrawer = drawerDoc.querySelector('#CartDrawer');
      var currCartDrawer = document.querySelector('#CartDrawer');
      if (newCartDrawer && currCartDrawer) {
        currCartDrawer.innerHTML = newCartDrawer.innerHTML;
        var overlay = currCartDrawer.querySelector('#CartDrawer-Overlay');
        if (overlay && drawer) {
          overlay.addEventListener('click', drawer.close.bind(drawer));
        }
      }
      var newDrawerEl = drawerDoc.querySelector('cart-drawer');
      if (newDrawerEl && drawer) {
        drawer.classList.toggle('is-empty', newDrawerEl.classList.contains('is-empty'));
      }
    }

    var bubbleHtml = state.sections['el-cart-icon-bubble'];
    if (bubbleHtml) {
      var bubbleDoc = new DOMParser().parseFromString(bubbleHtml, 'text/html');
      var newBubble = bubbleDoc.querySelector('.shopify-section');
      var currBubble = document.getElementById('el-cart-icon-bubble');
      if (newBubble && currBubble) {
        currBubble.innerHTML = newBubble.innerHTML;
      }
    }

    if (drawer && typeof window.initCartUpsellCarousel === 'function') {
      window.initCartUpsellCarousel(drawer);
    }
  };

  // Called by el-cart.js after a drawer quantity change. Returns updated state
  // (with sections) for the caller to apply, or null.
  window.syncFreeGiftAfterCartChange = async function (options) {
    options = options || {};
    return reconcile(
      options.cart,
      options.sections || DRAWER_SECTIONS,
      options.sectionsUrl || window.location.pathname
    );
  };

  // Fetches the current cart, reconciles, and applies the result to the drawer.
  window.syncFreeGiftFromDrawer = async function () {
    if (!getConfig()) return null;
    if (!document.getElementById('CartDrawer')) return null;

    try {
      var cartResponse = await fetch('/cart.js');
      if (!cartResponse.ok) return null;

      var cart = await cartResponse.json();
      var state = await reconcile(cart, DRAWER_SECTIONS, window.location.pathname);

      if (state && state.sections) {
        window.applyFreeGiftCartSections(state);
      }

      return state;
    } catch (error) {
      console.error('[FreeGift]', error);
      return null;
    }
  };

  function scheduleDrawerSync() {
    clearTimeout(_drawerSyncTimer);
    _drawerSyncTimer = setTimeout(function () {
      window.syncFreeGiftFromDrawer().catch(function (error) {
        console.error('[FreeGift]', error);
      });
    }, 150);
  }

  document.addEventListener('DOMContentLoaded', function () {
    if (!getConfig()) return;

    scheduleDrawerSync();

    var drawer = document.querySelector('cart-drawer');
    if (!drawer) return;

    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        if (mutations[i].attributeName === 'class' && drawer.classList.contains('active')) {
          scheduleDrawerSync();
          break;
        }
      }
    });

    observer.observe(drawer, { attributes: true, attributeFilter: ['class'] });
  });
})();
